import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  User,
  Hash,
  MapPin,
  Calendar,
  QrCode,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from "lucide-react";
import { format, parseISO } from "date-fns";

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [member, setMember] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [memberRes, checkInsRes, locationsRes] = await Promise.all([
          axios.get(`${API}/members/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/check-ins`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { member_id: id }
          }),
          axios.get(`${API}/locations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setMember(memberRes.data);
        setCheckIns(checkInsRes.data);
        setLocations(locationsRes.data);
      } catch (error) {
        console.error("Error fetching member:", error);
        toast.error("Failed to load member details");
        navigate("/members");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token, navigate]);

  const getStatusBadge = (status) => {
    const styles = {
      active: "badge-active",
      suspended: "badge-suspended",
      expired: "badge-expired"
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.expired}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!member) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="member-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/members")} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {member.name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            {getStatusBadge(member.status)}
            <code className="text-sm bg-gray-100 px-2 py-1 rounded">
              {member.member_id}
            </code>
            <span className="text-gray-500">
              Member since {format(parseISO(member.created_at), "MMMM d, yyyy")}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Member Info */}
        <Card className="border-0 shadow-sm" data-testid="member-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-gray-400" />
              Member Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Hash className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Member ID</p>
                <p className="font-medium font-mono">{member.member_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <MapPin className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">
                  {member.location_ids?.length > 0
                    ? member.location_ids.map(id => getLocationName(id)).join(", ")
                    : "No location assigned"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                member.expiration_date && new Date(member.expiration_date) < new Date() 
                  ? "bg-red-100" 
                  : "bg-gray-100"
              }`}>
                <Calendar className={`h-4 w-4 ${
                  member.expiration_date && new Date(member.expiration_date) < new Date()
                    ? "text-red-500"
                    : "text-gray-500"
                }`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Membership Expires</p>
                {member.expiration_date ? (
                  <p className={`font-medium ${
                    new Date(member.expiration_date) < new Date() ? "text-red-600" : ""
                  }`}>
                    {format(parseISO(member.expiration_date), "MMMM d, yyyy")}
                    {new Date(member.expiration_date) < new Date() && (
                      <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                        Expired
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="font-medium text-gray-400">No expiration set</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Check-in</p>
                <p className="font-medium">
                  {member.last_check_in
                    ? format(parseISO(member.last_check_in), "MMMM d, yyyy h:mm a")
                    : "Never checked in"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Check-in History */}
        <Card className="lg:col-span-2 border-0 shadow-sm" data-testid="checkin-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-gray-400" />
              Check-in History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {checkIns.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Checked In By</TableHead>
                    <TableHead>Webhook</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkIns.map((checkIn) => (
                    <TableRow key={checkIn.id}>
                      <TableCell>
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {checkIn.code}
                        </code>
                      </TableCell>
                      <TableCell>{checkIn.location_name}</TableCell>
                      <TableCell>
                        {format(parseISO(checkIn.checked_in_at), "MMM d, yyyy h:mm a")}
                      </TableCell>
                      <TableCell>{checkIn.checked_in_by}</TableCell>
                      <TableCell>
                        {checkIn.webhook_sent ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="h-4 w-4" />
                            Sent
                          </span>
                        ) : checkIn.webhook_status === "failed" ? (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="h-4 w-4" />
                            Failed
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <QrCode className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No check-in history</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
